import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/api/client";
import { MessageCircle, Mail, Phone, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import SeoHead from "@/components/seo/SeoHead";

const Contact = () => {
  const { settings } = useSiteSettings();
  const wa = settings.whatsapp_number || "918090245875";
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true); toast.success("Message sent! We'll respond within 24 hours.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (ex) { toast.error(formatApiErrorDetail(ex.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="container-fah py-10 sm:py-16" data-testid="page-contact">
      <SeoHead
        title="Contact Us — Get in Touch"
        description="Have a question about an order or want to partner with FarmersAgroHub? Reach our support team via phone, email or WhatsApp. We respond within 24 hours."
        path="/contact"
      />
      <div className="text-center max-w-2xl mx-auto mb-10">
        <div className="text-xs uppercase tracking-[0.15em] text-primary font-semibold mb-2">Get In Touch</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold">We're here to help</h1>
        <p className="text-muted-foreground mt-3">Have a question, complaint, or want to partner with us? We respond in Hindi, English, Marathi & more.</p>
      </div>

      <div className="grid lg:grid-cols-[1fr_400px] gap-8 max-w-5xl mx-auto">
        <form onSubmit={submit} className="card-fah p-6 sm:p-8 space-y-4" data-testid="form-contact">
          <h2 className="font-display text-xl font-bold">Send us a message</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field testid="contact-name" label="Your Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <Field testid="contact-email" label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            <Field testid="contact-phone" label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <Field testid="contact-subject" label="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Message</label>
            <textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={5} className="mt-1 w-full px-3 py-2.5 border border-border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm" data-testid="contact-message" />
          </div>
          <button disabled={busy} type="submit" className="bg-primary hover:bg-primary-hover text-white font-semibold py-2.5 px-6 rounded-lg flex items-center gap-2 disabled:opacity-50" data-testid="btn-send-contact">
            <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send Message"}
          </button>
          {sent && <div className="text-sm text-green-700 bg-green-50 p-3 rounded-lg">✓ Thank you! We've received your message and will respond within 24 hours.</div>}
        </form>

        <div className="space-y-4">
          <a href={`https://wa.me/${wa}?text=Hi%2C%20I%20need%20help%20with%20FarmersAgroHub`} target="_blank" rel="noreferrer" className="card-fah p-5 flex items-center gap-3 hover-lift bg-[#25D366]/10" data-testid="contact-whatsapp">
            <div className="w-12 h-12 rounded-xl bg-[#25D366] text-white flex items-center justify-center"><MessageCircle className="w-6 h-6" /></div>
            <div>
              <div className="font-semibold">WhatsApp Support</div>
              <div className="text-xs text-muted-foreground">Click to chat instantly</div>
              <div className="text-sm font-bold text-primary mt-0.5">{settings.contact_phone}</div>
            </div>
          </a>
          <a href={`mailto:${settings.contact_email}`} className="card-fah p-5 flex items-center gap-3 hover-lift" data-testid="contact-email-link">
            <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Mail className="w-6 h-6" /></div>
            <div>
              <div className="font-semibold">Email Support</div>
              <div className="text-sm text-primary">{settings.contact_email}</div>
              <div className="text-xs text-muted-foreground">Response within 24 hours</div>
            </div>
          </a>
          <a href={`tel:+${wa}`} className="card-fah p-5 flex items-center gap-3 hover-lift" data-testid="contact-phone-link">
            <div className="w-12 h-12 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center"><Phone className="w-6 h-6" /></div>
            <div>
              <div className="font-semibold">Call Us</div>
              <div className="text-sm text-secondary">{settings.contact_phone}</div>
              <div className="text-xs text-muted-foreground">Mon–Sat, 9am – 7pm IST</div>
            </div>
          </a>
          <div className="card-fah p-5 flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-muted text-foreground flex items-center justify-center shrink-0"><MapPin className="w-6 h-6" /></div>
            <div>
              <div className="font-semibold">Office</div>
              <div className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{settings.contact_address}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, testid, ...props }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</label>
    <input data-testid={testid} {...props} className="mt-1 w-full px-3 py-2.5 border border-border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm" />
  </div>
);

export default Contact;
